import { Dayjs } from 'dayjs'

import { getOnlyDate, isHoliday } from '~/astra'
import { DayColumn, DayColumnWithComment } from '~/components'
import { useStore } from '~/store/store'
import { useActivePractitioners, usePractitionerScheduleStatuses } from '~/storm'

type Props = {
    date: Dayjs
}

function useAvailablePractitionerCountForSurgery(date: Dayjs): number {
    const practitioners = useActivePractitioners()
    const statusesByPractitionerId = usePractitionerScheduleStatuses({
        where: { scheduled_date: getOnlyDate(date) },
        include: { definition: true },
        groupBy: 'practitioner_id',
    })

    return practitioners.filter(practitioner => {
        const practitionerStatuses = statusesByPractitionerId.get(practitioner.id) ?? []
        return (
            practitionerStatuses.length === 0 ||
            practitionerStatuses.some(status => status.definition?.assignment === 'surgery') ||
            practitionerStatuses.some(status => status.definition?.assignment === 'on_call_surgery') ||
            practitionerStatuses.every(status => status.status_code === 'EVENING_POLICLINIC')
        )
    }).length
}

export const DateHeaderCell = ({ date }: Props) => {
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const availablePractitioners = useAvailablePractitionerCountForSurgery(date)

    // This is a very hard-coded and temporary solution.
    // Ortho got a new operating room in June. They need 13 surgeons before June and 15 after.
    // Ent requires 5 surgeons.
    const requiredPractitioners = departmentKey === 1 ? (date.month() >= 5 ? 15 : 13) : 5

    const hasSufficientAvailablePractitioners = isHoliday(date) || availablePractitioners >= requiredPractitioners

    return (
        <div className={`relative flex w-full justify-center p-1 ${!hasSufficientAvailablePractitioners ? '!bg-caution-subtle-rest' : ''}`}>
            {hasSufficientAvailablePractitioners ? (
                <DayColumnWithComment date={date} isHoliday={isHoliday(date)} />
            ) : (
                <div className="flex w-full cursor-pointer justify-center">
                    <DayColumn
                        warning={`For få kirurger til å fylle alle stuer (${availablePractitioners} av ${requiredPractitioners})`}
                        date={date}
                        isHoliday={false}
                    />
                </div>
            )}
        </div>
    )
}
